import React from 'react';
import { Drawer, Descriptions, Button, Space, Spin, message, Select, Modal } from 'antd';
import { useRequest } from 'ahooks';
import type { FaxMessageDetailVo, SentFaxStatus } from 'types/eFax';
import {
    faxDetail,
    faxMarkRead,
    faxDownload,
    faxAssignPatient,
    faxRemovePatient,
    faxConfirmPatient,
} from 'api/fax';
import { getProspectList } from 'api/prospect';
import type { Prospect } from 'types/common';
import { isFaxScheduleSuccess, formatFaxNumber } from 'utils/eFaxScheduleRows';
import { formatUTCTime } from 'utils/common';
import s from '../s.module.less';

const SENT_STATUS_LABEL_MAP: Record<SentFaxStatus, string> = {
    processing: 'Processing',
    delivered: 'Delivered',
    cancelled: 'Cancelled',
    failed: 'Failed',
    chargeback: 'Chargeback',
    refunded: 'Refunded',
    paid: 'Paid',
};

const tagToString = (tag: FaxMessageDetailVo['tag']): string | undefined => {
    if (!tag || tag.length === 0) return undefined;
    const s = tag.filter(Boolean).join(', ');
    return s || undefined;
};

const normalizeSentStatus = (raw: string | undefined): SentFaxStatus => {
    const s = (raw || '').toLowerCase();
    if (['processing', 'delivered', 'cancelled', 'failed', 'chargeback', 'refunded', 'paid'].includes(s)) {
        return s as SentFaxStatus;
    }
    if (s.includes('deliver') || s === 'success') return 'delivered';
    if (s.includes('fail') || s.includes('error')) return 'failed';
    if (s.includes('cancel')) return 'cancelled';
    return 'processing';
};

const getStatusDisplay = (direction: string | undefined, statusRaw: string | undefined): string => {
    const raw = (statusRaw || '').trim();
    const dir = (direction || '').toLowerCase();
    const isInbound = dir === 'inbound';
    if (isInbound) {
        return raw ? raw.toLowerCase() : '-';
    }
    const normalized = normalizeSentStatus(raw);
    return SENT_STATUS_LABEL_MAP[normalized] || raw || '-';
};

const FaxDetailDescriptions: React.FC<{
    detail: FaxMessageDetailVo;
    statusDisplay: string;
    tags: string;
    hasPatient: boolean;
}> = ({ detail, statusDisplay, tags, hasPatient }) => {
    return (
        <Descriptions column={1} bordered size="small" labelStyle={{ width: 140, fontWeight: 500 }}>
            <Descriptions.Item label="Direction">{detail.direction || '-'}</Descriptions.Item>
            <Descriptions.Item label="Status">{statusDisplay}</Descriptions.Item>
            <Descriptions.Item label="Subject">{detail.subject || '-'}</Descriptions.Item>
            <Descriptions.Item label="Sender fax">
                {detail.senderFax ? formatFaxNumber(detail.senderFax) || detail.senderFax : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Sender name">{detail.senderName || '-'}</Descriptions.Item>
            <Descriptions.Item label="Recipient fax">
                {detail.recipientFax ? formatFaxNumber(detail.recipientFax) || detail.recipientFax : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Recipient name">{detail.recipientName || '-'}</Descriptions.Item>
            <Descriptions.Item label="Pages">{detail.pages ?? '-'}</Descriptions.Item>
            <Descriptions.Item label="Sent at">
                {detail.sentAt ? formatUTCTime(detail.sentAt, 'MM/DD/YY') : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Completed at">
                {detail.completedAt ? formatUTCTime(detail.completedAt, 'MM/DD/YY') : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Tag">{tags}</Descriptions.Item>
            <Descriptions.Item label="AI summary">{detail.aiSummary || '-'}</Descriptions.Item>
            <Descriptions.Item label="Patient">
                {detail.assignedClient?.name || (hasPatient ? `ID: ${detail.assignedClient?.clientId}` : '-')}
            </Descriptions.Item>
            <Descriptions.Item label="Notes">{detail.notes || '-'}</Descriptions.Item>
        </Descriptions>
    );
};

const FaxDetailExtra: React.FC<{
    isInbound: boolean;
    hasPatient: boolean;
    isAiPatient: boolean;
    onDownload: () => void;
    onConfirmPatient: () => void;
    onRemovePatient: () => void;
    onOpenAssign: () => void;
}> = ({ isInbound, hasPatient, isAiPatient, onDownload, onConfirmPatient, onRemovePatient, onOpenAssign }) => {
    if (!isInbound) {
        return (
            <Space>
                <Button onClick={onDownload}>Download</Button>
            </Space>
        );
    }

    return (
        <Space>
            <Button onClick={onDownload}>Download</Button>
            {hasPatient ? (
                <>
                    {isAiPatient ? (
                        <Button type="primary" onClick={onConfirmPatient}>
                            Confirm patient
                        </Button>
                    ) : null}
                    <Button danger onClick={onRemovePatient}>
                        Remove patient
                    </Button>
                </>
            ) : (
                <Button type="primary" onClick={onOpenAssign}>
                    Assign patient
                </Button>
            )}
        </Space>
    );
};

interface Props {
    open: boolean;
    uuid: string | null;
    onClose: () => void;
    onUpdated?: (uuid: string) => void;
}

const FaxDetailDrawer: React.FC<Props> = ({ open, uuid, onClose, onUpdated }) => {
    const [detail, setDetail] = React.useState<FaxMessageDetailVo | null>(null);
    const [loading, setLoading] = React.useState(false);
    const [assignLoading, setAssignLoading] = React.useState(false);
    const [showAssignModal, setShowAssignModal] = React.useState(false);
    const [selectedPatientId, setSelectedPatientId] = React.useState<number | undefined>(undefined);

    const { data: prospectData, run: fetchProspects } = useRequest(getProspectList, { manual: true });
    const prospectList: Prospect[] = (prospectData?.data?.data as Prospect[] | undefined) || [];
    const clientOptions = prospectList.filter((p) => !p.isSample);

    const fetchDetail = React.useCallback(async (id: string) => {
        setLoading(true);
        try {
            const result = await faxDetail(id);
            const body = result?.data as { code?: number; data?: FaxMessageDetailVo } | undefined;
            if (!result?.error && body && isFaxScheduleSuccess(body)) {
                const vo = body.data ?? null;
                setDetail(vo);
                // auto mark-read
                if (vo?.isRead === false) {
                    await faxMarkRead(id).catch(() => {});
                    onUpdated?.(id);
                }
            }
        } finally {
            setLoading(false);
        }
    }, [onUpdated]);

    React.useEffect(() => {
        if (open && uuid) {
            setDetail(null);
            fetchDetail(uuid);
        }
    }, [open, uuid, fetchDetail]);

    const handleDownload = async () => {
        if (!uuid) return;
        const result = await faxDownload(uuid);
        const body = result?.data as { code?: number; data?: string } | undefined;
        if (!result?.error && body && isFaxScheduleSuccess(body) && body.data) {
            window.open(body.data, '_blank');
        } else {
            message.error('Download failed');
        }
    };

    const handleOpenAssign = () => {
        fetchProspects();
        setSelectedPatientId(undefined);
        setShowAssignModal(true);
    };

    const handleConfirmAssign = async () => {
        if (!uuid || !selectedPatientId) return;
        setAssignLoading(true);
        try {
            const result = await faxAssignPatient(uuid, selectedPatientId);
            const body = result?.data as { code?: number; message?: string } | undefined;
            if (result?.error || !body || !isFaxScheduleSuccess(body)) {
                message.error(body?.message || 'Failed to assign patient');
                return;
            }
            message.success('Patient assigned');
            setShowAssignModal(false);
            if (onUpdated && uuid) onUpdated(uuid);
            fetchDetail(uuid);
        } finally {
            setAssignLoading(false);
        }
    };

    const handleRemovePatient = async () => {
        if (!uuid) return;
        Modal.confirm({
            title: 'Remove patient',
            content: 'Remove the assigned patient from this fax?',
            okText: 'Remove',
            okButtonProps: { danger: true },
            onOk: async () => {
                const result = await faxRemovePatient(uuid);
                const body = result?.data as { code?: number; message?: string } | undefined;
                if (result?.error || !body || !isFaxScheduleSuccess(body)) {
                    message.error(body?.message || 'Failed to remove patient');
                    return;
                }
                message.success('Patient removed');
                if (onUpdated) onUpdated(uuid);
                fetchDetail(uuid);
            },
        });
    };

    const handleConfirmPatient = async () => {
        if (!uuid) return;
        const result = await faxConfirmPatient(uuid);
        const body = result?.data as { code?: number; message?: string } | undefined;
        if (result?.error || !body || !isFaxScheduleSuccess(body)) {
            message.error(body?.message || 'Failed to confirm patient');
            return;
        }
        message.success('Patient confirmed');
        if (onUpdated) onUpdated(uuid);
        fetchDetail(uuid);
    };

    const isInbound = detail?.direction === 'INBOUND' || detail?.direction === 'inbound';
    const hasPatient = !!detail?.assignedClient?.clientId;
    const isAiPatient = hasPatient && !detail?.assignedClient?.name;

    const tags = tagToString(detail?.tag) ?? '-';
    const statusDisplay = getStatusDisplay(detail?.direction, detail?.status);

    let drawerBody: React.ReactNode = null;
    if (detail) {
        drawerBody = (
            <FaxDetailDescriptions
                detail={detail}
                statusDisplay={statusDisplay}
                tags={tags}
                hasPatient={hasPatient}
            />
        );
    } else if (!loading) {
        drawerBody = <div style={{ color: '#6b7280', textAlign: 'center', padding: 32 }}>No detail available</div>;
    }

    return (
        <>
            <Drawer
                title="Fax detail"
                open={open}
                onClose={onClose}
                width={520}
                className={s.faxDetailDrawer}
                extra={
                    <FaxDetailExtra
                        isInbound={Boolean(isInbound)}
                        hasPatient={hasPatient}
                        isAiPatient={Boolean(isAiPatient)}
                        onDownload={handleDownload}
                        onConfirmPatient={handleConfirmPatient}
                        onRemovePatient={handleRemovePatient}
                        onOpenAssign={handleOpenAssign}
                    />
                }
                destroyOnClose
            >
                <Spin spinning={loading}>
                    {drawerBody}
                </Spin>
            </Drawer>

            <Modal
                title="Assign patient"
                open={showAssignModal}
                onCancel={() => setShowAssignModal(false)}
                onOk={handleConfirmAssign}
                confirmLoading={assignLoading}
                okText="Assign"
                destroyOnClose
            >
                <Select
                    showSearch
                    style={{ width: '100%' }}
                    placeholder="Search and select patient"
                    optionFilterProp="label"
                    value={selectedPatientId}
                    onChange={(val) => setSelectedPatientId(val)}
                    options={clientOptions.map((p) => ({
                        label: `${p.firstName} ${p.lastName}`,
                        value: p.id,
                    }))}
                />
            </Modal>
        </>
    );
};

export default FaxDetailDrawer;
